import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import axios from 'axios'
import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'
import { getWexVcnTransactions } from 'enrollment-platform/api/vcn'
import { VCN_BASE_PATH } from 'routes/Employees/Employee/EmployeeCoverage/modules/employee-benefits/api/vcn-service'
import { combineAndSortTransactions } from 'routes/Employees/Employee/EmployeeCoverage/modules/employee-benefits/api/vcn-service/mergeVcTransactions'

import { GetVcnTransactionsResponseDto } from './dto/get-vcn-transaction.dto'
import { GetAchIstreamTransactionResponseDto } from './dto/get-ach-istream-transaction.dto'

export type { GetPaymentAssignmentTaskSummaryParams } from './payment-assignment-api-client'

export const getAchIstreamTransactions = async (
  benefitId: string
): Promise<GetAchIstreamTransactionResponseDto[]> => {
  const response = await apiClient.get<GetAchIstreamTransactionResponseDto[]>(
    `/payment-transaction/istream/${benefitId}`
  )
  return response.data
}

export const useAchIstreamTransactionsQuery = (
  benefitId: string | null,
  options?: Omit<
    UseQueryOptions<
      GetAchIstreamTransactionResponseDto[],
      Error,
      GetAchIstreamTransactionResponseDto[]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    GetAchIstreamTransactionResponseDto[],
    Error,
    GetAchIstreamTransactionResponseDto[]
  >({
    queryKey: ['ach-istream-transactions', benefitId],
    queryFn: () => getAchIstreamTransactions(benefitId as string),
    enabled: !!benefitId,
    ...options,
  })
}

export const getVcnTransactions = async (
  benefitId: string,
  options?: { merchantLogId?: string | null }
): Promise<GetVcnTransactionsResponseDto[]> => {
  const merchantLogId = options?.merchantLogId
  const marqetaPromise = axios
    .get(`${VCN_BASE_PATH}/card/transactions/sourcePublicKey/${benefitId}`)
    .then((response) => response.data)
    .catch(() => ({ transactions: [] }))

  const wexPromise =
    merchantLogId != null && merchantLogId !== ''
      ? getWexVcnTransactions(merchantLogId).catch(() => ({
          mergedTransactions: [],
        }))
      : Promise.resolve({ mergedTransactions: [] })

  const [marqetaRes, wexRes] = await Promise.all([marqetaPromise, wexPromise])
  return combineAndSortTransactions(marqetaRes, wexRes, [])
}

export const useVcnTransactionsQuery = (
  benefitId: string | null,
  merchantLogId?: string | null,
  options?: Omit<
    UseQueryOptions<
      GetVcnTransactionsResponseDto[],
      Error,
      GetVcnTransactionsResponseDto[]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    GetVcnTransactionsResponseDto[],
    Error,
    GetVcnTransactionsResponseDto[]
  >({
    queryKey: ['vcn-transactions', benefitId, merchantLogId ?? ''],
    queryFn: () => getVcnTransactions(benefitId as string, { merchantLogId }),
    enabled: !!benefitId,
    ...options,
  })
}

export {
  getPaymentAssignment,
  getPaymentAssignmentDetail,
  getPaymentAssignmentBenefitEvents,
  getPaymentAssignmentUsers,
  getPaymentCarrierList,
  getPaymentCarrierListNames,
  assignPaymentAssignments,
  setPaymentAssignmentStatus,
  batchClearPaymentAssignmentStatus,
  batchAssignByBenefitId,
  getPaymentAssignmentUserRole,
  getPaymentAssignmentTaskSummary,
  createPaymentBotTask,
  bulkCreatePaymentBotTask,
  bulkCancelPaymentBotTasks,
  cancelPaymentBotTaskByPaymentAssignmentId,
  usePaymentAssignmentDetailQuery,
  usePaymentAssignmentBenefitEventsQuery,
  usePaymentAssignmentUsersQuery,
  usePaymentCarrierListQuery,
  usePaymentCarrierListNameQuery,
  useAssignPaymentAssignmentsMutation,
  useSetPaymentAssignmentStatusMutation,
  useBatchClearPaymentAssignmentStatusMutation,
  usePaymentAssignmentQuery,
  usePaymentAssignmentUserRoleQuery,
  usePaymentAssignmentTaskSummaryQuery,
  useCreatePaymentBotTaskMutation,
  useBulkCreatePaymentBotTaskMutation,
  useBulkCancelPaymentBotTaskMutation,
  useCancelPaymentBotTaskMutation,
} from './payment-assignment-api-client'
